import { Check, ChevronLeft, Search, X, type LucideIcon, Video } from 'lucide-react-native';
import { cssInterop } from 'nativewind';

/**
 * Wrap a lucide icon so it accepts a `className` prop and propagates the
 * resolved `color` / `width` / `height` styles to the icon's native props.
 *
 * Lucide icons (and the underlying `react-native-svg` primitives) don't
 * inherit color from a parent className the way HTML SVG does with CSS
 * `currentColor` — each icon needs an explicit `color` prop. `cssInterop`
 * bridges that gap once per icon at module load: NativeWind resolves
 * `text-typography-900` into `style.color`, and the `nativeStyleToProp`
 * mapping forwards it as the `color` prop the icon already understands.
 *
 * Apply per icon at module scope (not at render time) so the registration
 * happens once and isn't repeated by every consumer.
 */
function withClassName<T extends LucideIcon>(Icon: T): T {
  // The cssInterop overload is fussy about generic components, so we cast on
  // the way in and out. The runtime behavior is the same: NativeWind reads
  // className, resolves it into a style object, and re-routes `color`/`width`/
  // `height` to the icon's native props.
  return cssInterop(Icon as LucideIcon, {
    className: {
      target: 'style',
      nativeStyleToProp: { color: true, width: true, height: true },
    },
  }) as unknown as T;
}

export const CheckIcon = withClassName(Check);
export const ChevronLeftIcon = withClassName(ChevronLeft);
export const SearchIcon = withClassName(Search);
export const VideoIcon = withClassName(Video);
export const XIcon = withClassName(X);
